import { isEmpty } from 'lodash'

import { IMovedBooking, IMovedBookingTrip } from 'features/Bookings/types'
import {
  GTBookingV2TicketAncillary,
  GTBookingV2Passenger,
  GTBookingV2TicketStatus,
  GTCreateBookingTripChangeProposalResponse,
  GTCreateBookingTripChangeProposalResponseTrip,
  GTBookingV2TicketServiceFee,
  GTBookingV2CouponDiscountType,
  GTBookingV2TicketAncillaryType,
  GTBookingV2Ticket,
  GTAncillaryType
} from 'gql/types.generated'

export const mapTripProposalToBookingTrip = (
  tripProposal: GTCreateBookingTripChangeProposalResponseTrip
): IMovedBookingTrip => {
  return {
    id: tripProposal.id,
    segment: {
      departureAt: tripProposal.segment.departureAt,
      id: tripProposal.segment.id,
      origin: {
        id: tripProposal.segment.origin.id,
        name: tripProposal.segment.origin.name,
        city: {
          id: tripProposal.segment.origin.city.id,
          name: tripProposal.segment.origin.city.name,
          timezone: tripProposal.segment.origin.city.timezone
        }
      },
      destination: {
        id: tripProposal.segment.destination.id,
        name: tripProposal.segment.destination.name,
        city: {
          id: null,
          name: '',
          timezone: null
        }
      }
    }
  }
}

const getOrderedTripsProposal = (
  bookingProposal: GTCreateBookingTripChangeProposalResponse,
  oldBooking: IMovedBooking,
  oldTrip: IMovedBookingTrip
): GTCreateBookingTripChangeProposalResponseTrip[] => {
  if (bookingProposal.trips.length === 1) {
    return bookingProposal.trips
  }
  const isDepartureTripModified = oldBooking.departureTrips.some(({ id }) => id === oldTrip.id)
  if (isDepartureTripModified) {
    const returnTripHasSameOrder = oldBooking.returnTrips.some(({ id }) => id === bookingProposal.trips[1].id)
    return returnTripHasSameOrder ? bookingProposal.trips : bookingProposal.trips.reverse()
  } else {
    const departureTripHasSameOrder = oldBooking.departureTrips.some(({ id }) => id === bookingProposal.trips[0].id)
    return departureTripHasSameOrder ? bookingProposal.trips : bookingProposal.trips.reverse()
  }
}

const mapAncillaryTypeToBookingV2AncillaryType = (
  type: GTAncillaryType
): GTBookingV2TicketAncillaryType | undefined => {
  switch (type) {
    case GTAncillaryType.Luggage:
      return GTBookingV2TicketAncillaryType.Luggage
    case GTAncillaryType.CarryOn:
      return GTBookingV2TicketAncillaryType.CarryOn
    case GTAncillaryType.Seat:
      return GTBookingV2TicketAncillaryType.Seat
    case GTAncillaryType.ServiceFee:
      return GTBookingV2TicketAncillaryType.ServiceFee
    case GTAncillaryType.Insurance:
      return GTBookingV2TicketAncillaryType.Insurance
  }
}

export const mapBookingProposalToBookingQuery = (
  bookingProposal: GTCreateBookingTripChangeProposalResponse,
  oldBooking: IMovedBooking,
  oldTrip: IMovedBookingTrip
): IMovedBooking => {
  const newBooking = { ...oldBooking }
  const orderedTrips = getOrderedTripsProposal(bookingProposal, oldBooking, oldTrip)
  const departureTripProposal = orderedTrips[0]
  newBooking.departureTrips = [mapTripProposalToBookingTrip(departureTripProposal)]
  if (orderedTrips.length > 1) {
    newBooking.returnTrips = [mapTripProposalToBookingTrip(orderedTrips[1])]
  }
  const oldConfirmedTickets = oldBooking.tickets.filter((ticket) => ticket.status !== GTBookingV2TicketStatus.Cancelled)
  newBooking.tickets = oldConfirmedTickets.map((oldTicket) => {
    const newTicket = bookingProposal.tickets.find(({ id }) => id === oldTicket.id)
    if (isEmpty(newTicket)) {
      throw new Error(`Ticket ${oldTicket.id} not found in booking proposal`)
    }
    const newTrip = orderedTrips.find(({ id }) => id === newTicket.tripID)
    const passenger = oldTicket.passenger as GTBookingV2Passenger
    return {
      id: oldTicket.id,
      isReturn: isEmpty(newBooking.departureTrips.find(({ id }) => id === newTicket.tripID)),
      subtotal: newTicket.subtotal,
      vat: newTicket.vat,
      discount: newTicket.discount,
      status: oldTicket.status,
      isCheckedIn: oldTicket.isCheckedIn,
      passenger: {
        id: passenger?.id || '',
        firstName: passenger?.firstName || '',
        lastName: passenger?.lastName || '',
        type: passenger?.type,
        properties: passenger?.properties || []
      },
      seat: {
        id: newTicket.seat.id,
        label: newTicket.seat.label
      },
      ancillaries: newTicket.ancillaries.map((ancillary) => {
        return {
          id: ancillary.id,
          type: mapAncillaryTypeToBookingV2AncillaryType(ancillary.type) as GTBookingV2TicketAncillaryType,
          quantity: ancillary.quantity,
          total: ancillary.total,
          unitPrice: ancillary.unitPrice
        }
      }) as GTBookingV2TicketAncillary[],
      serviceFee: newTicket.serviceFee as GTBookingV2TicketServiceFee,
      trip: {
        id: newTicket.tripID,
        segment: {
          id: newTrip?.segment.id || ''
        }
      },
      payments: oldBooking.payments.filter((payment) => {
        return payment.tickets.find(({ id }) => id === newTicket.id)
      })
    }
  })
  newBooking.coupon = bookingProposal.coupon
    ? {
        id: bookingProposal.coupon.id,
        code: bookingProposal.coupon.code,
        value: bookingProposal.coupon.value,
        discountType: bookingProposal.coupon.discountType as GTBookingV2CouponDiscountType
      }
    : undefined
  return newBooking
}

export const calculateAncillariesTotals = (
  ancillaries: GTBookingV2TicketAncillary[]
): {
  qty: number
  amount: number
} =>
  ancillaries.reduce(
    (acc, ancillary) => {
      acc.qty += ancillary.quantity ?? 0
      acc.amount += ancillary.total ?? 0
      return acc
    },
    {
      qty: 0,
      amount: 0
    }
  )

export const getAncillariesByType = (
  ticket: GTBookingV2Ticket,
  type: GTBookingV2TicketAncillaryType
): GTBookingV2TicketAncillary[] /*TODO: NA-10843*/ =>
  ticket.ancillaries?.filter((ancillary) => ancillary.type === type) || []
